import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

// Services mega-menu structure. Kept colocated so it's a single source of truth.
const SERVICES_MENU = [
  {
    heading: 'Core Services',
    links: [
      { label: 'Plumbing Services', to: '/services/plumbing' },
      { label: 'Emergency Plumber', to: '/services/emergency-plumber' },
      { label: 'Handyman Services', to: '/services/handyman' },
      { label: 'Property Maintenance', to: '/services/property-maintenance' },
    ],
  },
  {
    heading: 'Secondary Services',
    links: [
      { label: 'Air Conditioning', to: '/services/air-conditioning' },
      { label: 'Refrigeration', to: '/services/refrigeration' },
    ],
  },
];

// Top-level nav items that should jump to a homepage section anchor.
const SECTION_LINKS = [
  { label: 'About', id: 'about' },
  { label: 'Why Us', id: 'why-choose-us' },
  { label: 'Our Work', id: 'projects' },
  { label: 'Contact', id: 'contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  const servicesWrapRef = useRef(null);
  const closeTimer = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close desktop dropdown on outside click / Escape.
  useEffect(() => {
    if (!isServicesOpen) return undefined;
    const onClick = (e) => {
      if (servicesWrapRef.current && !servicesWrapRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setIsServicesOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isServicesOpen]);

  // Menu-closing on route change is handled imperatively via onClick on
  // each Link/button. No effect needed here (avoids set-state-in-effect lint).

  // Navigate to a homepage section anchor. Smooth-scrolls when already on
  // the homepage, otherwise routes to /#section so HomePage can scroll on mount.
  const goToSection = useCallback(
    (id) => {
      setIsMobileMenuOpen(false);
      setIsServicesOpen(false);
      setIsMobileServicesOpen(false);
      if (isHome) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(`/#${id}`);
      }
    },
    [isHome, navigate]
  );

  const handleServicesHoverOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsServicesOpen(true);
  };
  const handleServicesHoverClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsServicesOpen(false), 120);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gray-950/95 backdrop-blur-md shadow-lg' : 'bg-gray-950/80 backdrop-blur-sm'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex-shrink-0"
            data-testid="header-logo-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_engineered-fast/artifacts/pqs8nrxs_Untitled%20design%20%281%29.png"
              alt="H.A.R.P Solutions LTD"
              className="h-20 md:h-24 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => goToSection('about')}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200"
              data-testid="nav-about"
            >
              About
            </button>

            {/* Services dropdown trigger */}
            <div
              ref={servicesWrapRef}
              className="relative"
              onMouseEnter={handleServicesHoverOpen}
              onMouseLeave={handleServicesHoverClose}
            >
              <button
                type="button"
                onClick={() => setIsServicesOpen((v) => !v)}
                className="flex items-center gap-1 text-gray-300 hover:text-amber-500 transition-colors duration-200"
                aria-haspopup="true"
                aria-expanded={isServicesOpen}
                data-testid="nav-services-trigger"
              >
                Services
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isServicesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isServicesOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                  data-testid="nav-services-dropdown"
                >
                  <div className="w-[520px] max-w-[92vw] bg-gray-950 border border-gray-800 rounded-xl shadow-2xl p-6 grid grid-cols-2 gap-6">
                    {SERVICES_MENU.map((col) => (
                      <div key={col.heading}>
                        <h3 className="text-amber-500 text-xs font-semibold uppercase tracking-wider mb-3">
                          {col.heading}
                        </h3>
                        <ul className="space-y-2">
                          {col.links.map((link, idx) => (
                            <li key={`${col.heading}-${link.label}-${idx}`}>
                              <Link
                                to={link.to}
                                onClick={() => setIsServicesOpen(false)}
                                className="text-sm text-gray-300 hover:text-amber-500 transition-colors block"
                                data-testid={`nav-services-link-${col.heading
                                  .toLowerCase()
                                  .replace(/[^a-z0-9]+/g, '-')}-${idx}`}
                              >
                                {link.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => goToSection('why-choose-us')}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200"
              data-testid="nav-why-us"
            >
              Why Us
            </button>
            <button
              onClick={() => goToSection('projects')}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200"
              data-testid="nav-our-work"
            >
              Our Work
            </button>
            <button
              onClick={() => goToSection('contact')}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="tel:+443301221094"
              className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-md transition-all duration-300 hover:shadow-lg font-medium"
              data-testid="header-call-now"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden bg-gray-900/98 backdrop-blur-md border-t border-gray-800 max-h-[calc(100vh-5rem)] overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-2">
            <button
              onClick={() => goToSection('about')}
              className="block w-full text-left text-gray-300 hover:text-amber-500 py-2 transition-colors"
              data-testid="mobile-nav-about"
            >
              About
            </button>

            {/* Mobile Services accordion */}
            <div className="border-b border-gray-800/60 pb-2">
              <button
                type="button"
                onClick={() => setIsMobileServicesOpen((v) => !v)}
                className="w-full flex items-center justify-between text-gray-300 hover:text-amber-500 py-2 transition-colors"
                aria-expanded={isMobileServicesOpen}
                data-testid="mobile-nav-services-trigger"
              >
                <span>Services</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isMobileServicesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isMobileServicesOpen && (
                <div className="pl-3 pt-2 pb-3 space-y-4" data-testid="mobile-nav-services-panel">
                  {SERVICES_MENU.map((col) => (
                    <div key={`m-${col.heading}`}>
                      <p className="text-amber-500 text-[11px] font-semibold uppercase tracking-wider mb-1">
                        {col.heading}
                      </p>
                      <ul className="space-y-1">
                        {col.links.map((link, idx) => (
                          <li key={`m-${col.heading}-${link.label}-${idx}`}>
                            <Link
                              to={link.to}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="block text-sm text-gray-300 hover:text-amber-500 py-1"
                              data-testid={`mobile-nav-services-link-${col.heading
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, '-')}-${idx}`}
                            >
                              {link.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {SECTION_LINKS.filter((s) => s.id !== 'about').map((s) => (
              <button
                key={s.id}
                onClick={() => goToSection(s.id)}
                className="block w-full text-left text-gray-300 hover:text-amber-500 py-2 transition-colors"
                data-testid={`mobile-nav-${s.id}`}
              >
                {s.label}
              </button>
            ))}

            <Button
              onClick={() => goToSection('contact')}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-md transition-all mt-4"
              data-testid="mobile-nav-quote-cta"
            >
              Request a Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
